import React, { useState, useEffect } from 'react';
import { Veiculo } from '../../types/veiculo';
import { veiculoExtraService } from '../../services/veiculoExtraService';
import { Wrench, Calendar, AlertTriangle, CheckCircle2, Clock, X, ShieldAlert } from 'lucide-react';

interface Props {
  veiculo: Veiculo | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const VeiculoManutencaoModal: React.FC<Props> = ({
  veiculo,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [status, setStatus] = useState<'disponivel' | 'em_manutencao' | 'em_uso' | 'desativado'>('disponivel');
  const [motivo, setMotivo] = useState('');
  const [previsao, setPrevisao] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (veiculo) {
      if (veiculo.ativo === false) {
        setStatus('desativado');
      } else {
        setStatus((veiculo.status_frota as any) || 'disponivel');
      }
      setMotivo(veiculo.motivo_manutencao || '');
      setPrevisao(veiculo.previsao_entrega ? veiculo.previsao_entrega.split('T')[0] : '');
      setErrorMsg('');
    }
  }, [veiculo]);

  if (!isOpen || !veiculo) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'em_manutencao' && !motivo.trim()) {
      setErrorMsg('Por favor, informe o motivo da manutenção.');
      return;
    }

    setLoading(true);
    setErrorMsg('');
    try {
      await veiculoExtraService.updateStatusManutencao(
        veiculo.id,
        status,
        status === 'em_manutencao' ? motivo : null,
        status === 'em_manutencao' ? previsao : null
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao atualizar status do veículo:', err);
      setErrorMsg(err.message || 'Erro ao atualizar status do veículo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-amber-500/20 rounded-lg border border-amber-500/30 text-amber-400">
              <Wrench className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base leading-snug">Status & Manutenção da Frota</h3>
              <p className="text-xs text-slate-400">
                Veículo: <span className="font-semibold text-white">{veiculo.placa || 'Sem Placa'}</span> (ID #{veiculo.id})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
              {errorMsg}
            </div>
          )}

          {/* Seleção do Status */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-2">
              Selecione a Situação Atual do Veículo
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              {/* Disponível */}
              <button
                type="button"
                onClick={() => setStatus('disponivel')}
                className={`p-3 rounded-xl border text-left transition-all flex items-start gap-2.5 ${
                  status === 'disponivel'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-500/20'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${status === 'disponivel' ? 'text-emerald-600' : 'text-slate-400'}`} />
                <div>
                  <div className="text-xs font-bold uppercase">Disponível</div>
                  <div className="text-[11px] opacity-75">Pronto para operação</div>
                </div>
              </button>

              {/* Em Manutenção */}
              <button
                type="button"
                onClick={() => setStatus('em_manutencao')}
                className={`p-3 rounded-xl border text-left transition-all flex items-start gap-2.5 ${
                  status === 'em_manutencao'
                    ? 'border-amber-500 bg-amber-50 text-amber-900 ring-2 ring-amber-500/20'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Wrench className={`w-4 h-4 mt-0.5 shrink-0 ${status === 'em_manutencao' ? 'text-amber-600' : 'text-slate-400'}`} />
                <div>
                  <div className="text-xs font-bold uppercase">Em Manutenção</div>
                  <div className="text-[11px] opacity-75">Oficina / Reparo</div>
                </div>
              </button>

              {/* Em Uso */}
              <button
                type="button"
                onClick={() => setStatus('em_uso')}
                className={`p-3 rounded-xl border text-left transition-all flex items-start gap-2.5 ${
                  status === 'em_uso'
                    ? 'border-blue-500 bg-blue-50 text-blue-900 ring-2 ring-blue-500/20'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Clock className={`w-4 h-4 mt-0.5 shrink-0 ${status === 'em_uso' ? 'text-blue-600' : 'text-slate-400'}`} />
                <div>
                  <div className="text-xs font-bold uppercase">Em Viagem / Uso</div>
                  <div className="text-[11px] opacity-75">Alocado em rota</div>
                </div>
              </button>

              {/* Desativado */}
              <button
                type="button"
                onClick={() => setStatus('desativado')}
                className={`p-3 rounded-xl border text-left transition-all flex items-start gap-2.5 ${
                  status === 'desativado'
                    ? 'border-rose-500 bg-rose-50 text-rose-900 ring-2 ring-rose-500/20'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <ShieldAlert className={`w-4 h-4 mt-0.5 shrink-0 ${status === 'desativado' ? 'text-rose-600' : 'text-slate-400'}`} />
                <div>
                  <div className="text-xs font-bold uppercase">Inativo / Desativado</div>
                  <div className="text-[11px] opacity-75">Bloqueado da frota</div>
                </div>
              </button>
            </div>
          </div>

          {/* Seção condicional para Manutenção */}
          {status === 'em_manutencao' && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
              <div>
                <label className="block text-xs font-bold text-amber-900 uppercase mb-1">
                  Motivo da Manutenção *
                </label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex: Troca de pastilhas de freio, revisão de 50.000km, alinhamento..."
                  rows={2}
                  className="w-full text-xs p-2.5 border border-amber-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-amber-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-amber-900 uppercase mb-1 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-amber-700" />
                  Previsão de Entrega / Término
                </label>
                <input
                  type="date"
                  value={previsao}
                  onChange={(e) => setPrevisao(e.target.value)}
                  className="w-full text-xs p-2 border border-amber-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-lg shadow transition-colors flex items-center gap-2"
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
